import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import './PackageDetails.css';

// Defines a consistent order for displaying the days of the week
const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const PackageDetails = () => {
    const { packageId } = useParams();
    const navigate = useNavigate();
    const [user, authLoading] = useAuthState(auth);
    const [packageData, setPackageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI State for Daily Grubs style interface
    const [mainImage, setMainImage] = useState(null);
    const [thumbnailImages, setThumbnailImages] = useState([]);
    
    // Subscription state
    const [subscriptionDuration, setSubscriptionDuration] = useState('3');
    const [excludeWeekends, setExcludeWeekends] = useState(false);
    const [isSubscribing, setIsSubscribing] = useState(false);

    // Calendar state
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedDates, setSelectedDates] = useState([]);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    useEffect(() => {
        if (!packageId) {
            setLoading(false);
            setError("No Package ID was found in the URL.");
            return;
        }

        const fetchPackageDetails = async () => {
            setLoading(true);
            try {
                const packageDocRef = doc(db, 'packages', packageId);
                const packageDocSnap = await getDoc(packageDocRef);

                if (!packageDocSnap.exists()) {
                    throw new Error('This package could not be found.');
                }

                const rawPackageData = packageDocSnap.data();
                
                if (rawPackageData.imageUrl) {
                    setMainImage(rawPackageData.imageUrl);
                    setThumbnailImages([rawPackageData.imageUrl]);
                }

                const finalPackageData = { ...rawPackageData, id: packageId };
                setPackageData(finalPackageData);

            } catch (err) {
                console.error("Error fetching package details:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPackageDetails();
    }, [packageId]);

    // Calendar functions
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        
        const days = Array(firstDayOfMonth).fill(null);
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        return days;
    };

    const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const isDateSelected = (date) => selectedDates.some(selectedDate => selectedDate.toDateString() === date.toDateString());
    const isDateDisabled = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    const handleDateClick = (date) => {
        if (isDateDisabled(date)) return;

        const duration = parseInt(subscriptionDuration);
        const newSelectedDates = [];
        let currentDate = new Date(date);
        
        while (newSelectedDates.length < duration) {
            if (excludeWeekends) {
                const dayOfWeek = currentDate.getDay();
                if (dayOfWeek === 0 || dayOfWeek === 6) {
                    currentDate.setDate(currentDate.getDate() + 1);
                    continue;
                }
            }
            newSelectedDates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        setSelectedDates(newSelectedDates);
        setShowCalendar(false);
    };

    const handleDurationClick = (duration) => {
        setSubscriptionDuration(duration);
        setSelectedDates([]);
        setShowCalendar(true);
    };

    const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));

    const calculateTotalPrice = () => {
        if (!packageData) return 0;
        let total = packageData.price * parseInt(subscriptionDuration);
        if (parseInt(subscriptionDuration) >= 7) {
            total *= 0.95; // 5% discount
        }
        return Math.round(total);
    };

    const handleSubscribe = async () => {
        if (!user) {
            alert('Please login to subscribe!');
            return;
        }
        if (selectedDates.length === 0) {
            alert('Please select your subscription start date!');
            return;
        }
        setIsSubscribing(true);
        try {
            const subscriptionRequest = {
                customerId: user.uid,
                customerName: user.displayName || user.email,
                packageId: packageData.id,
                packageName: packageData.name,
                subscriptionDuration: subscriptionDuration,
                selectedDates: selectedDates.map(date => date.toISOString()),
                totalPrice: calculateTotalPrice(),
                requestDate: new Date(),
                requestStatus: 'pending',
            };
            await addDoc(collection(db, 'package_request'), subscriptionRequest);
            alert('Subscription request submitted successfully!');
        } catch (error) {
            console.error('Error submitting subscription:', error);
            alert('Error submitting subscription. Please try again.');
        } finally {
            setIsSubscribing(false);
        }
    };

    if (loading || authLoading) return <div className="status-container">Loading...</div>;
    if (error) return <div className="status-container error">Error: {error}</div>;
    if (!packageData) return <div className="status-container">No data found.</div>;

    return (
        <div className="package-details-page">
            <nav className="breadcrumbs">Home / Packages / {packageData.name}</nav>

            <section className="package-hero-section">
                {/* This section remains the same */}
                 <div className="package-hero-content">
                    <div className="package-image-section">
                        <div className="main-image-wrapper">
                            {mainImage ? (
                                <img src={mainImage} alt={packageData.name} className="main-package-image" />
                            ) : (
                                <div className="main-image-placeholder">No Image</div>
                            )}
                        </div>
                    </div>
                    
                    <div className="package-info-section">
                        <p className="package-category">MEAL PACKAGE</p>
                        <h1 className="package-title">{packageData.name}</h1>
                        <p className="package-price">₹{packageData.price || 'N/A'} <span className="price-unit">/ month</span></p>
                        <p className="package-description">{packageData.description}</p>
                    </div>
                </div>
            </section>

            {/* RESTRUCTURED Subscription Section */}
            <section className="subscription-section-cookr">
                <div className="subscription-step">
                    <h2 className="step-title">1. Choose Your Subscription</h2>
                    <div className="duration-buttons">
                        <button 
                            className={`duration-btn ${subscriptionDuration === '3' ? 'active' : ''}`}
                            onClick={() => handleDurationClick('3')}
                        >
                            3 DAYS (Trial)
                            <span className="new-tag">NEW</span>
                        </button>
                        <button 
                            className={`duration-btn ${subscriptionDuration === '7' ? 'active' : ''}`}
                            onClick={() => handleDurationClick('7')}
                        >
                            7 DAYS
                        </button>
                        <button 
                            className={`duration-btn ${subscriptionDuration === '30' ? 'active' : ''}`}
                            onClick={() => handleDurationClick('30')}
                        >
                            30 DAYS
                        </button>
                    </div>
                </div>

                {showCalendar && (
                    <div className="subscription-step">
                         <h2 className="step-title">2. Select a Start Date</h2>
                        <div className="calendar-dropdown">
                             <div className="calendar-header">
                                <button onClick={prevMonth} className="calendar-nav-btn">‹</button>
                                <h4>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h4>
                                <button onClick={nextMonth} className="calendar-nav-btn">›</button>
                            </div>
                            <div className="calendar-grid">
                                <div className="calendar-weekdays">
                                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => <span key={i}>{day}</span>)}
                                </div>
                                <div className="calendar-days">
                                    {getDaysInMonth(currentMonth).map((date, index) => (
                                        <button
                                            key={index}
                                            className={`calendar-day ${!date ? 'empty' : ''} ${date && isDateDisabled(date) ? 'disabled' : ''} ${date && isDateSelected(date) ? 'selected' : ''}`}
                                            onClick={() => date && handleDateClick(date)}
                                            disabled={!date || isDateDisabled(date)}
                                        >
                                            {date ? date.getDate() : ''}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="calendar-footer">
                                <p>Select a start date to auto-select {subscriptionDuration} consecutive days.</p>
                            </div>
                        </div>
                    </div>
                )}
                
                <div className="subscription-step">
                    <h2 className="step-title">3. Exclude Days (Optional)</h2>
                     <div className="exclude-checkboxes">
                        <label className="exclude-checkbox">
                            <input type="checkbox" checked={excludeWeekends} onChange={(e) => setExcludeWeekends(e.target.checked)} />
                            <span>Exclude Weekends (Saturday & Sunday)</span>
                        </label>
                    </div>
                </div>

                <div className="subscription-summary-cookr">
                    <h3 className="summary-title">Your Subscription Details</h3>
                    <div className="summary-item">
                        <span className="summary-label">Package:</span>
                        <span className="summary-value">{packageData.name}</span>
                    </div>
                     <div className="summary-item">
                        <span className="summary-label">Duration:</span>
                        <span className="summary-value">{subscriptionDuration} Days</span>
                    </div>
                    {selectedDates.length > 0 && (
                        <div className="summary-item">
                            <span className="summary-label">Starts On:</span>
                            <span className="summary-value">{formatDate(selectedDates[0])}</span>
                        </div>
                    )}
                    <div className="price-breakdown">
                        <div className="price-item">
                            <span>Base Price:</span>
                            <span>₹{packageData.price} x {subscriptionDuration} days</span>
                        </div>
                        {parseInt(subscriptionDuration) >= 7 && (
                            <div className="price-item discount">
                                <span>Weekly Discount (5%):</span>
                                <span>- ₹{Math.round(packageData.price * parseInt(subscriptionDuration) * 0.05)}</span>
                            </div>
                        )}
                        <div className="price-total">
                            <span>Total Price:</span>
                            <span>₹{calculateTotalPrice()}</span>
                        </div>
                    </div>
                    <button 
                        className="checkout-btn"
                        onClick={handleSubscribe}
                        disabled={isSubscribing || selectedDates.length === 0}
                    >
                        {isSubscribing ? 'Processing...' : 'Proceed to Checkout'}
                    </button>
                </div>
            </section>
        </div>
    );
};

export default PackageDetails;